/**
 * Exchanges Service for CoinVAT API
 * Handles exchange integrations, trading sessions, and account management
 */
import { apiClient } from './api-client';
import type {
  ExchangeAccountConnection,
  ExchangeAccountResponse,
  ExchangeAccountUpdate,
  ExchangeTestConnection,
  ExchangeBalanceResponse,
  ExchangeOrderResponse,
  ExchangeOrderParams,
  TradingSessionCreate,
  TradingSessionResponse,
} from './types';

export class ExchangesService {
  /**
   * Test exchange API connection without saving credentials
   */
  async testConnection(connectionData: ExchangeAccountConnection): Promise<ExchangeTestConnection> {
    return apiClient.post<ExchangeTestConnection>('/api/v1/exchanges/test-connection', connectionData);
  }

  /**
   * Connect exchange account to user profile
   */
  async connectAccount(accountData: ExchangeAccountConnection): Promise<ExchangeAccountResponse> {
    return apiClient.post<ExchangeAccountResponse>('/api/v1/exchanges/connect', accountData);
  }

  /**
   * List user's connected exchange accounts
   */
  async listAccounts(): Promise<ExchangeAccountResponse[]> {
    return apiClient.get<ExchangeAccountResponse[]>('/api/v1/exchanges/');
  }

  /**
   * Get specific exchange account details
   */
  async getAccount(accountId: number): Promise<ExchangeAccountResponse> {
    return apiClient.get<ExchangeAccountResponse>(`/api/v1/exchanges/${accountId}`);
  }

  /**
   * Update exchange account settings
   */
  async updateAccount(accountId: number, updateData: ExchangeAccountUpdate): Promise<ExchangeAccountResponse> {
    return apiClient.put<ExchangeAccountResponse>(`/api/v1/exchanges/${accountId}`, updateData);
  }

  /**
   * Disconnect exchange account
   */
  async disconnectAccount(accountId: number): Promise<{ message: string }> {
    return apiClient.delete<{ message: string }>(`/api/v1/exchanges/${accountId}`);
  }

  /**
   * Get exchange account balance
   */
  async getAccountBalance(accountId: number): Promise<ExchangeBalanceResponse> {
    return apiClient.get<ExchangeBalanceResponse>(`/api/v1/exchanges/${accountId}/balance`);
  }

  /**
   * Get open orders from exchange
   */
  async getOpenOrders(accountId: number, params?: ExchangeOrderParams): Promise<ExchangeOrderResponse[]> {
    const queryParams = new URLSearchParams();
    if (params?.symbol) queryParams.append('symbol', params.symbol);
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.offset) queryParams.append('offset', params.offset.toString());
    
    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `/api/v1/exchanges/${accountId}/orders?${queryString}`
      : `/api/v1/exchanges/${accountId}/orders`;
    
    return apiClient.get<ExchangeOrderResponse[]>(endpoint);
  }

  // Trading Sessions Management

  /**
   * List user's trading sessions
   */
  async listTradingSessions(): Promise<TradingSessionResponse[]> {
    return apiClient.get<TradingSessionResponse[]>('/api/v1/exchanges/trading-sessions');
  }

  /**
   * Create a new trading session
   */
  async createTradingSession(sessionData: TradingSessionCreate): Promise<TradingSessionResponse> {
    return apiClient.post<TradingSessionResponse>('/api/v1/exchanges/trading-sessions', sessionData);
  }

  /**
   * Stop a trading session
   */
  async stopTradingSession(sessionId: string): Promise<TradingSessionResponse> {
    return apiClient.put<TradingSessionResponse>(`/api/v1/exchanges/trading-sessions/${sessionId}/stop`);
  }

  // Utility methods for exchange management

  /**
   * Get verified exchange accounts only
   */
  async getVerifiedAccounts(): Promise<ExchangeAccountResponse[]> {
    try {
      const accounts = await this.listAccounts();
      return accounts.filter(account => account.is_verified);
    } catch (error) {
      console.error('Error getting verified accounts:', error);
      return [];
    }
  }

  /**
   * Get accounts by exchange name
   */
  async getAccountsByExchange(exchangeName: string): Promise<ExchangeAccountResponse[]> {
    try {
      const accounts = await this.listAccounts();
      return accounts.filter(account => 
        account.exchange.toLowerCase() === exchangeName.toLowerCase()
      );
    } catch (error) {
      console.error('Error getting accounts by exchange:', error);
      return [];
    }
  }

  /**
   * Enable/disable trading for an account
   */
  async toggleAccountTrading(accountId: number, enabled: boolean): Promise<ExchangeAccountResponse> {
    return this.updateAccount(accountId, { trading_enabled: enabled });
  }

  /**
   * Get total portfolio value across all exchange accounts
   */
  async getTotalExchangePortfolioValue(): Promise<number> {
    try {
      const accounts = await this.listAccounts();
      let totalValue = 0;

      // Get balance for each verified account
      for (const account of accounts) {
        if (account.is_verified) {
          try {
            const balance = await this.getAccountBalance(account.id);
            totalValue += balance.total_usd_value;
          } catch (error) {
            console.warn(`Failed to get balance for account ${account.id}:`, error);
          }
        }
      }

      return totalValue;
    } catch (error) {
      console.error('Error calculating total exchange portfolio value:', error);
      return 0;
    }
  }

  /**
   * Get active trading sessions
   */
  async getActiveTradingSessions(): Promise<TradingSessionResponse[]> {
    try {
      const sessions = await this.listTradingSessions();
      return sessions.filter(session => session.status === 'active');
    } catch (error) {
      console.error('Error getting active trading sessions:', error);
      return [];
    }
  }

  /**
   * Get trading session statistics
   */
  async getTradingSessionStats(): Promise<{
    total_sessions: number;
    active_sessions: number;
    total_trades: number;
    successful_trades: number;
    success_rate: number;
  }> {
    try {
      const sessions = await this.listTradingSessions();
      
      const totalSessions = sessions.length;
      const activeSessions = sessions.filter(s => s.status === 'active').length;
      const totalTrades = sessions.reduce((sum, s) => sum + s.total_trades, 0);
      const successfulTrades = sessions.reduce((sum, s) => sum + s.successful_trades, 0);
      const successRate = totalTrades > 0 ? (successfulTrades / totalTrades) * 100 : 0;

      return {
        total_sessions: totalSessions,
        active_sessions: activeSessions,
        total_trades: totalTrades,
        successful_trades: successfulTrades,
        success_rate: Math.round(successRate * 100) / 100, // Round to 2 decimal places
      };
    } catch (error) {
      console.error('Error getting trading session stats:', error);
      return {
        total_sessions: 0,
        active_sessions: 0,
        total_trades: 0,
        successful_trades: 0,
        success_rate: 0,
      };
    }
  }

  /**
   * Check if user has any connected exchanges
   */
  async hasConnectedExchanges(): Promise<boolean> {
    try {
      const accounts = await this.listAccounts();
      return accounts.length > 0;
    } catch (error) {
      console.error('Error checking connected exchanges:', error);
      return false;
    }
  }

  /**
   * Validate exchange account connection
   */
  async validateAccountConnection(accountId: number): Promise<boolean> {
    try {
      const account = await this.getAccount(accountId);
      return account.is_verified && account.last_successful_connection !== null;
    } catch (error) {
      console.error('Error validating account connection:', error);
      return false;
    }
  }
}

// Export singleton instance
export const exchangesService = new ExchangesService();